import React, { useState, useEffect, useRef } from "react";
import { debounce } from "../../utils/debounce";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { FiMenu, FiX, FiSearch, FiUser, FiLogOut } from "react-icons/fi";
import { useAuth } from "../../hooks/useAuth";
import { useCart } from "../../context/cartContext";

const ProfileAvatar = ({ user }) => {
  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    return parts.length === 1
      ? parts[0].charAt(0).toUpperCase()
      : (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  return (
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-sm font-bold">
        {getInitials(user?.name)}
      </div>
      <span className="hidden md:block text-sm font-medium text-gray-700 truncate max-w-[120px]">
        {user?.name?.split(" ")[0]}
      </span>
    </div>
  );
};

const Navbar = () => {
  const navigate = useNavigate();
  const { isAuthenticated, getUser, logout, loading: authLoading } = useAuth();
  const { itemCount, loadCart } = useCart();
  const user = getUser();
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debouncedNavigateRef = useRef();

  useEffect(() => {
    debouncedNavigateRef.current = debounce((value) => {
      if (value.trim()) {
        navigate(`/shop?search=${encodeURIComponent(value.trim())}`);
        setIsSearchOpen(false);
        setIsMobileSearchOpen(false);
      }
    }, 700);
  }, [navigate]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchValue.trim())}`);
      setIsSearchOpen(false);
      setIsMobileSearchOpen(false);
      setSearchValue("");
    }
  };

  const handleDebouncedSearch = (e) => {
    setSearchValue(e.target.value);
    debouncedNavigateRef.current(e.target.value);
  };

  const profileLink = isAuthenticated() ? "/user-dashboard" : "/login";

  const handleLogout = async () => {
    await logout();
    loadCart();
    navigate("/login", { replace: true });
    setIsOpen(false);
  };

  const toggleMenu = () => setIsOpen(!isOpen);
  const toggleSearch = () => setIsSearchOpen(!isSearchOpen);
  const toggleMobileSearch = () => setIsMobileSearchOpen(!isMobileSearchOpen);

  const navLinks = [
    { name: "HOME", path: "/" },
    { name: "SHOP", path: "/shop" },
    { name: "MEN", path: "/men" },
    { name: "WOMEN", path: "/women" },
    { name: "KIDS", path: "/kids" },
    { name: "ABOUT", path: "/about" },
    { name: "CONTACT", path: "/contact" },
  ];

  return (
    <>
      {authLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center">
          <div className="text-center">
            <img
              src={process.env.PUBLIC_URL + "/images/loaders/block-loader.svg"}
              alt="Loading..."
              className="w-12 h-12 mx-auto"
            />
          </div>
        </div>
      )}
      <nav
        className="fixed top-0 left-0 right-0 z-50 bg-[#EDECEA] min-h-auto border-b-[0.5px] border-[#1C1A19]"
        aria-label="Main navigation"
      >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-4">
        <div className="flex justify-between items-center h-20 sm:h-24 gap-4">
          {/* Left - Navigation Links */}
          <div className="hidden lg:flex flex-1 space-x-4 xl:space-x-8">
            {navLinks.map((link) => (
              <NavLink
                key={link.name}
                to={link.path}
                className={({ isActive }) =>
                  `text-gray-700 hover:text-black text-xs sm:text-sm lg:text-base transition duration-300 ease-in-out whitespace-nowrap ${
                    isActive ? "font-bold text-black" : "font-medium"
                  }`
                }
              >
                {link.name}
              </NavLink>
            ))}
          </div>

          {/* Center - Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src="/images/logo/logo-dark.png"
              alt="Trulixx Logo"
              className="h-12 sm:h-16 w-20 sm:w-28"
            />
          </Link>

          {/* Right - Icons */}
          <div className="flex flex-1 justify-end items-center space-x-4">
            {/* Desktop Search */}
            <div className="relative flex items-center">
              {isSearchOpen && (
                <form
                  onSubmit={handleSearchSubmit}
                  className="absolute right-0 flex items-center bg-white px-3 py-2 gap-2 border border-black animate-in fade-in slide-in-from-right-4 z-50"
                >
                  <FiSearch size={20} className="text-[#D2BDA1]" />
                  <input
                    type="text"
                    placeholder="Search..."
                    className="bg-transparent outline-none text-sm w-48"
                    autoFocus
                    value={searchValue}
                    onChange={handleDebouncedSearch}
                  />
                  <button
                    type="button"
                    onClick={toggleSearch}
                    className="active:scale-95 active:duration-75"
                  >
                    <FiX size={20} />
                  </button>
                </form>
              )}
              {/* Desktop search icon */}
              {!isSearchOpen && (
                <button
                  onClick={toggleSearch}
                  className="hidden lg:block p-2 hover:bg-gray-200 rounded-full transition active:scale-95 active:duration-75"
                >
                  <FiSearch size={20} />
                </button>
              )}
            </div>

            {/* Mobile Search Icon */}
            <button
              onClick={toggleMobileSearch}
              className="block lg:hidden p-2 hover:bg-gray-200 rounded-full transition active:scale-95 active:duration-75"
              aria-label="Open mobile search"
            >
              <FiSearch size={20} />
            </button>

            {/* Mobile Search Popup */}
            {isMobileSearchOpen && (
              <div className="fixed inset-0 z-50 flex justify-center items-start pt-8 pointer-events-none">
                <div className="bg-white rounded-lg shadow-lg p-4 flex items-center gap-2 w-11/12 max-w-md pointer-events-auto">
                  <FiSearch size={20} className="text-[#D2BDA1]" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    className="bg-transparent outline-none text-base flex-1"
                    autoFocus
                    value={searchValue}
                    onChange={handleDebouncedSearch}
                  />
                  <button
                    type="button"
                    onClick={toggleMobileSearch}
                    className="active:scale-95 active:duration-75 absolute top-2 right-2"
                  >
                    <FiX size={22} />
                  </button>
                </div>
              </div>
            )}

            {/* Profile */}
            <Link
              to={profileLink}
              className="hidden lg:flex items-center p-2 rounded-full hover:bg-gray-200 transition"
            >
              {isAuthenticated() && user ? (
                <ProfileAvatar user={user} />
              ) : (
                <FiUser size={20} />
              )}
            </Link>

            {/* Cart Icon with Live Badge */}
            <button
              type="button"
              className="p-2 rounded-full hover:bg-gray-200 transition relative"
              onClick={() => {
                if (isOpen) setIsOpen(false);
                navigate("/cart");
              }}
              aria-label="View cart"
            >
              <img
                src="/images/icons/cart-icon.png"
                alt="Cart"
                className="w-5 h-5"
              />
              {itemCount > 0 && (
                <span className="absolute -top-[-2px] -right-[-2px] bg-[#556B2F] text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold border border-white">
                  {itemCount > 9 ? "9+" : itemCount}
                </span>
              )}
            </button>

            {/* Mobile Toggle */}
            <button
              onClick={toggleMenu}
              className="lg:hidden p-2 rounded-full hover:bg-gray-200 transition active:scale-95 active:duration-75"
            >
              {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden pb-4 border-t border-gray-200">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="block px-4 py-3 text-gray-700 hover:bg-gray-100"
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className="border-t border-black mt-2 pt-2">
              <Link
                to={profileLink}
                className="flex items-center gap-2 px-4 py-3"
                onClick={() => setIsOpen(false)}
              >
                <FiUser size={18} /> My Account
              </Link>
              {isAuthenticated() && (
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-3 text-red-500 font-bold w-full text-left active:scale-95 active:duration-75"
                >
                  <FiLogOut size={18} /> Sign Out
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};

export default Navbar;
